import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  ExternalLink, 
  FileText, 
  FileEdit,
  DollarSign,
  Calendar,
  Users,
  Vote,
  Scale,
  Loader2,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  Bookmark,
  Share2,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../App';
import Chip, { getStatusVariant } from '../components/Chip';
import { formatCommitteeName, getCommitteeChampberDisplay } from '../utils/committeeUtils';
import { NoteCountBadge } from '../components/notes';
import CustomFieldBadge from '../components/custom-fields/CustomFieldBadge';
import WorkspaceSelectionModal from '../components/WorkspaceSelectionModal';
import VotePredictionsModal from '../components/workspace/VotePredictions/VotePredictionsModal';
import type { WorkspacePermissions } from '../types/workspace';
import StopTrackingConfirmDialog from '../components/workspace/StopTrackingConfirmDialog';
import { getPartyTextColor } from '../utils/partyUtils';
import { useMediaQuery } from '../hooks/useMediaQuery';
import MobileMenuPage from '../components/MobileMenuPage';
import AISummarySection from '../components/billDetail/AISummarySection';
import BillVoteSummary from '../components/billDetail/BillVoteSummary';
import StatCard, { StatCardRow } from '../components/StatCard';
import CalendarEventDisplay from '../components/shared/CalendarEventDisplay';
import LobbyingExplorer from '../components/lobbying/LobbyingExplorer';
import type { LobbyGroupOption } from '../types/lobbying';
import Button from '@shared/ui/Button';
import Tabs from '@shared/ui/Tabs';
import PageShell from '../components/PageShell';
import { formatDate } from '../utils/dateUtils';

const BILL_LOBBY_GROUP_OPTIONS: LobbyGroupOption[] = [
  { key: 'lobbyist', label: 'Lobbyist' },
  { key: 'client', label: 'Client' },
  { key: 'position', label: 'Position' },
];

interface BillSponsor {
  name: string;
  title: string;
  sponsor_type: string;
  party?: string;
  legislator_id?: string;
}

interface CommitteeVote {
  legislator_id: string;
  member_name: string;
  party?: string;
  legislator_chamber: string;
  vote: string;
}

interface CommitteeHearing {
  id: number;
  hearing_date: string;
  activity: string;
  vote: string;
  moved_by: string;
  seconded_by: string;
  action: string;
  summary_url: string;
  votes: CommitteeVote[];
}

interface BillCommittee {
  id: number;
  committee_id: number;
  name: string;
  chamber: string;
  hearings: CommitteeHearing[];
}

interface BillDocument {
  type: string;
  subtype: string;
  date: string;
  url: string;
}

interface BillAmendment {
  amendment_number: string;
  committee_hearing: string;
  status: string;
  url: string;
  date: string;
}

interface BillHistory {
  event_date: string;
  location: string;
  action: string;
}

interface BillMemberVote {
  member_name: string;
  vote: string;
  legislator_id?: string;
  party?: string;
}

interface BillChamberVote {
  id: number;
  chamber: string;
  date: string;
  action: string;
  motion: string;
  aye: number;
  no: number;
  excused: number;
  absent: number;
  vote_type?: string;
  memberVotes: BillMemberVote[];
}

interface BillSessionLaw {
  effective_date: string;
  chapter: string;
  title: string;
  document: string;
}

interface BillFiscalNote {
  id: string;
  date: string;
  version: string;
  url: string;
}

interface BillLobbyist {
  id: number;
  lobbyist_id: number;
  lobbyist_name: string;
  lobbyist_firm_name: string;
  client_name: string;
  position: string;
  report_due_date?: string;
}

interface LobbyistGroup {
  key: string;
  latest: BillLobbyist;
  history: BillLobbyist[];
}

interface BillDetail {
  id: string;
  number: string;
  title: string;
  concerning: string;
  session: string;
  subject: string | string[];
  summary: string;
  latest_action: string;
  latest_location: string;
  latest_date: string;
  chamber: string;
  created_at: string;
  introduced_date?: string;
}

interface AISummaryData {
  short_summary: string | null;
  long_summary: string | null;
  pro_position_summary: string | null;
  con_position_summary: string | null;
  created_at?: string;
  enabled_features?: {
    ai_summary_short: boolean;
    ai_summary_long: boolean;
    ai_summary_pro: boolean;
    ai_summary_con: boolean;
  };
}

interface NextCalendarEvent {
  calendar_id: number;
  reading_date: string;
  reading_time: string | null;
  reading_title: string;
  room_number: string | null;
  chamber: string;
  upon_adjournment: boolean;
}

interface BillDetailResponse {
  bill: BillDetail;
  sponsors: BillSponsor[];
  committees: BillCommittee[];
  documents: BillDocument[];
  amendments: BillAmendment[];
  history: BillHistory[];
  chamberVotes: BillChamberVote[];
  sessionLaws: BillSessionLaw[];
  fiscalNotes: BillFiscalNote[];
  lobbyists: BillLobbyist[];
  aiSummary?: AISummaryData | null;
  nextCalendarEvent?: NextCalendarEvent | null;
}

/** Axios / fetch abort — do not surface as a bill load failure (Strict Mode, remount, org refetch). */
function isRequestCanceledError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; name?: string };
  return e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError';
}


const BillDetailPage: React.FC = () => {
  const { id, billNumber } = useParams<{ id: string; billNumber: string }>();
  const billIdentifier = id || billNumber; // Support both route params
  const navigate = useNavigate();
  const location = useLocation();
  const [billData, setBillData] = useState<BillDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('history');
  const [expandedVotes, setExpandedVotes] = useState<Set<number>>(new Set());
  const [expandedCommitteeHearings, setExpandedCommitteeHearings] = useState<Set<number>>(new Set());
  const [expandedCommittees, setExpandedCommittees] = useState<Set<number>>(new Set());
  const lobbyistsTabRef = useRef<HTMLDivElement>(null);
  const votesTabRef = useRef<HTMLDivElement>(null);
  
  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Bookmark modal state
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [showVotePredictionsModal, setShowVotePredictionsModal] = useState(false);
  
  // Remove bill / stop tracking state
  const [showStopTrackingDialog, setShowStopTrackingDialog] = useState(false);
  const [isRemovingBill, setIsRemovingBill] = useState(false);
  
  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 1023px)');
  
  // Get user's organization ID from JWT token for AI summary settings
  // This is the most reliable source - no API call needed, always available
  const getUserOrgIdFromToken = (): string | null => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Skip citizen accounts (they use the citizen organization)
        if (payload.accountType === 'citizen' || 
            payload.organizationId === 'cccccccc-cccc-cccc-cccc-cccccccccccc') {
          return null;
        }
        return payload.organizationId || null;
      }
    } catch (e) {
      console.error('Error getting organizationId from token:', e);
    }
    return null;
  };
  
  const userOrgId = getUserOrgIdFromToken();
  
  // Track workspace organization for AI settings (overrides user org when in workspace)
  const [workspaceOrgId, setWorkspaceOrgId] = useState<string | null>(null);
  
  // Use workspace org if in workspace context, otherwise use user's org from token
  const organizationId = workspaceOrgId || userOrgId;
  

  // Workspace state
  const [workspaceInfo, setWorkspaceInfo] = useState<{ name: string } | null>(null);
  const [workspaceCanEdit, setWorkspaceCanEdit] = useState(false);
  /*
    The full permissions object, not just canEdit. /user-membership already
    returns the whole WorkspacePermissions shape — role, canView, canEdit,
    canManage, canInvite, canDelete — and this page was discarding all but one
    boolean. VotePredictionsModal needs `role` (to distinguish a guest) and
    `canView`.
  */
  const [workspacePermissions, setWorkspacePermissions] =
    useState<WorkspacePermissions | null>(null);

  // Get workspace ID from URL params for notes functionality
  const urlParams = new URLSearchParams(location.search);
  const fromWorkspace = urlParams.get('workspace');


  const fetchBillDetail = useCallback(
    async (billId: string, signal: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);
        const params = organizationId ? `?organizationId=${organizationId}` : '';
        const response = await api.get(`/bills/${billId}${params}`, { signal });

        if (response.data.success) {
          setError(null);
          setBillData(response.data.data);
        } else {
          setError('Failed to load bill details');
        }
      } catch (error: unknown) {
        if (isRequestCanceledError(error)) {
          return;
        }
        console.error('Error fetching bill details:', error);
        const err = error as { response?: { status?: number; data?: { message?: string } } };
        if (err.response?.status === 404) {
          setError(`Bill "${billId}" was not found.`);
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to load bill details. Please try again later.');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [organizationId]
  );

  useEffect(() => {
    if (!billIdentifier) return;
    const controller = new AbortController();
    void fetchBillDetail(billIdentifier, controller.signal);
    return () => controller.abort();
  }, [billIdentifier, fetchBillDetail]);

  // Fetch workspace info if we're in workspace context
  useEffect(() => {
    const fetchWorkspaceInfo = async () => {
      if (fromWorkspace) {
        try {
          const response = await api.get(`/workspaces/${fromWorkspace}`);
          const workspaceData = response.data?.data || response.data;
          if (workspaceData && workspaceData.name) {
            setWorkspaceInfo({ name: workspaceData.name });
          }
          // Capture workspace's organization ID for AI settings
          if (workspaceData && workspaceData.owner_org_id) {
            setWorkspaceOrgId(workspaceData.owner_org_id);
          }
          // Fetch user membership/permissions for this workspace
          try {
            const membershipResponse = await api.get(`/workspaces/${fromWorkspace}/user-membership`);
            const membershipData = membershipResponse.data?.data || membershipResponse.data;
            if (membershipData?.canEdit) {
              setWorkspaceCanEdit(true);
            }
            if (membershipData) {
              setWorkspacePermissions({
                canView: !!membershipData.canView,
                canEdit: !!membershipData.canEdit,
                canManage: !!membershipData.canManage,
                canInvite: !!membershipData.canInvite,
                canDelete: !!membershipData.canDelete,
                role: membershipData.role,
              });
            }
          } catch (permError) {
            console.error('Error fetching workspace permissions:', permError);
          }
        } catch (error) {
          console.error('Error fetching workspace info:', error);
        }
      } else {
        // Clear workspace org if not in workspace context
        setWorkspaceOrgId(null);
      }
    };

    fetchWorkspaceInfo();
  }, [fromWorkspace]);

  // Handle URL parameters for tab switching
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const sectionParam = urlParams.get('section'); // Mobile uses 'section' parameter
    
    // Desktop uses 'tab' parameter, mobile uses 'section' parameter
    const activeParam = isMobile ? sectionParam : tabParam;
    
    if (activeParam && tabs.some(tab => tab.id === activeParam)) {
      setActiveTab(activeParam);
      
      // If switching to lobbyists tab and data is loaded, scroll to the section
      if (activeParam === 'lobbyists' && billData && !loading) {
        setTimeout(() => {
          lobbyistsTabRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100); // Small delay to ensure DOM is updated
      }
      
      // If switching to votes tab and data is loaded, scroll to the section
      if (activeParam === 'votes' && billData && !loading) {
        setTimeout(() => {
          votesTabRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100); // Small delay to ensure DOM is updated
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, billData, loading, isMobile]);

  // Handle scrolling when data loads and we're already on lobbyists or votes tab
  useEffect(() => {
    if (billData && !loading) {
      if (activeTab === 'lobbyists') {
        setTimeout(() => {
          lobbyistsTabRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100);
      } else if (activeTab === 'votes') {
        setTimeout(() => {
          votesTabRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100);
      }
    }
  }, [billData, loading, activeTab]);

  // Scroll to top functionality
  const scrollToTop = () => {
    // Scroll both window and main container to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Also scroll main containers
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        break;
      }
    }
  };

  // Handle scroll event to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = container.scrollTop;
          break;
        }
      }
      
      // Show button if either window or container is scrolled
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after bill data loads
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!mainContainer) {
      retryTimer = setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [billData]); // Re-run when bill data changes


  const lobbyistGroups = useMemo((): LobbyistGroup[] => {
    const list = billData?.lobbyists;
    if (!list) return [];
    const groupMap = new Map<string, LobbyistGroup>();
    for (const l of list) {
      const key = `${l.lobbyist_name}||${l.client_name}`;
      const existing = groupMap.get(key);
      if (!existing) {
        groupMap.set(key, { key, latest: l, history: [] });
      } else {
        existing.history.push(l);
      }
    }
    return Array.from(groupMap.values());
  }, [billData?.lobbyists]);

  const latestLobbyistRecords = useMemo(() => {
    return lobbyistGroups.map(g => g.latest);
  }, [lobbyistGroups]);


  const getVoteColor = (vote: string) => {
    switch (vote.toUpperCase()) {
      case 'Y':
      case 'YES':
        return 'text-success-600 dark:text-success-400 font-semibold';
      case 'N':
      case 'NO':
        return 'text-error-600 dark:text-error-400 font-semibold';
      case 'E':
      case 'EXCUSED':
        return 'text-warning-600 dark:text-warning-400 font-semibold';
      case 'A':
      case 'ABSENT':
        return 'text-text-secondary dark:text-text-dark-secondary font-semibold';
      default:
        return 'text-text-secondary dark:text-text-dark-secondary';
    }
  };

  const formatVoteDisplay = (vote: string) => {
    switch (vote.toUpperCase()) {
      case 'Y':
        return 'Yes';
      case 'N':
        return 'No';
      case 'E':
        return 'Excused';
      case 'A':
        return 'Absent';
      default:
        return vote;
    }
  };

  const toggleVoteExpansion = (voteId: number) => {
    const newExpanded = new Set(expandedVotes);
    if (newExpanded.has(voteId)) {
      newExpanded.delete(voteId);
    } else {
      newExpanded.add(voteId);
    }
    setExpandedVotes(newExpanded);
  };

  const toggleCommitteeHearingExpansion = (hearingId: number) => {
    const newExpanded = new Set(expandedCommitteeHearings);
    if (newExpanded.has(hearingId)) {
      newExpanded.delete(hearingId);
    } else {
      newExpanded.add(hearingId);
    }
    setExpandedCommitteeHearings(newExpanded);
  };

  const toggleCommitteeExpansion = (committeeId: number) => {
    const newExpanded = new Set(expandedCommittees);
    if (newExpanded.has(committeeId)) {
      newExpanded.delete(committeeId);
    } else {
      newExpanded.add(committeeId);
    }
    setExpandedCommittees(newExpanded);
  };

  const tabs = [
    { id: 'history', label: 'Bill History', icon: Calendar },
    { id: 'text', label: 'Bill Text', icon: FileText },
    { id: 'amendments', label: 'Amendments', icon: FileEdit },
    { id: 'fiscal', label: 'Fiscal Notes', icon: DollarSign },
    { id: 'committees', label: 'Committees', icon: Users },
    { id: 'lobbyists', label: 'Lobbyists', icon: DollarSign },
    { id: 'votes', label: 'Votes', icon: Vote },
    { id: 'laws', label: 'Session Laws', icon: Scale }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !billData) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-text-tertiary dark:text-text-dark-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Bill Not Found
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            {error || `The bill "${billIdentifier}" could not be found. It may have been removed or the bill number may be incorrect.`}
          </p>
        </div>
      </div>
    );
  }

  const { bill, sponsors, committees, documents, amendments, history, chamberVotes, sessionLaws, fiscalNotes, lobbyists } = billData;

  // Bookmark handlers
  const handleTrackBillClick = () => {
    setShowBookmarkModal(true);
  };

  const handleBookmarkSuccess = (_workspaceIds: string[]) => {
    // You could add a toast notification here for better UX
  };

  const handleBookmarkModalClose = () => {
    setShowBookmarkModal(false);
  };

  // Remove bill from workspace handlers
  const handleRemoveBillClick = () => {
    setShowStopTrackingDialog(true);
  };

  const handleStopTrackingConfirm = async () => {
    if (!fromWorkspace || !billIdentifier) return;
    setIsRemovingBill(true);
    try {
      await api.delete(`/workspace-bill-tracking/${fromWorkspace}/${billIdentifier}`);
      toast.success('Bill removed from workspace');
      setShowStopTrackingDialog(false);
      // Navigate back to the workspace
      navigate(`/workspaces/${fromWorkspace}?tab=bill-tracking`);
    } catch (error) {
      console.error('Error removing bill from workspace:', error);
      toast.error('Failed to remove bill from workspace');
    } finally {
      setIsRemovingBill(false);
    }
  };

  const handleStopTrackingCancel = () => {
    setShowStopTrackingDialog(false);
  };
  
  // Debug logging (can be removed in production)
  // console.log('BillDetailPage: URL params:', { fromWorkspace, workspaceName, returnTo, fullSearch: location.search });

  return (
    <PageShell
      title={bill.number}
      /* No description: the card below already leads with the bill title, and
         repeating it in the header is the duplication this work removes. */
    >
      {/* Header Section - 4 Column Grid Layout */}
      <div className="card">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-6 gap-y-4">
          {/* Row 1: Bill Number, Status Chip, Action Buttons */}
          <div />
          <div className="lg:col-span-2 flex items-center">
            <Chip 
              label={bill.latest_action || 'Unknown Status'}
              variant={getStatusVariant(bill.latest_action || 'Unknown')}
              size="md"
            />
          </div>
          <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2">
            {/* Row 1 Action Buttons: Bill Text */}
            {documents.length > 0 && (
              <a
                href={documents[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-lg bg-background-tertiary text-text-primary hover:bg-background-quaternary dark:bg-background-dark-tertiary dark:text-text-dark-primary dark:hover:bg-background-dark-quaternary inline-flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Bill Text
              </a>
            )}
          </div>

          {/* Row 2: Bill Title (left) and Action Buttons (right) */}
          <h2 className="col-span-1 lg:col-span-2 text-2xl font-semibold text-text-primary dark:text-text-dark-primary leading-tight">
            {bill.title}
          </h2>
          <div className="col-span-1 lg:col-span-2 flex flex-wrap items-start justify-start lg:justify-end gap-2">
            {/* Workspace-only actions */}
            {fromWorkspace && (
              <NoteCountBadge
                entityType="bill"
                entityId={billIdentifier || ''}
                workspaceId={fromWorkspace}
                showNotesOnClick={true}
                variant="button"
              />
            )}
            {fromWorkspace && bill && (
              <CustomFieldBadge
                entityType="bill"
                entityId={billIdentifier || ''}
                entityDisplayName={`${bill.number}: ${bill.title}`}
                workspaceId={fromWorkspace}
                canEdit={workspaceCanEdit}
                variant="button"
              />
            )}
            {fromWorkspace && bill && (
              <Button
                onClick={() => setShowVotePredictionsModal(true)}
                variant="primary"
              className="inline-flex items-center gap-2"
                title="View vote predictions for this bill"
              >
                <Vote className="w-4 h-4" />
                Votes
              </Button>
            )}

            {/* General actions (both contexts) */}
            {!fromWorkspace && (
              <Button
                onClick={handleTrackBillClick}
                variant="primary"
              className="inline-flex items-center gap-2"
                title="Add this bill to workspace tracking"
              >
                <Bookmark className="w-4 h-4" />
                Track
              </Button>
            )}

            {bill && (
              <Button
                variant="secondary"
                onClick={() => {
                  const publicUrl = `${window.location.origin}/public/bills/${encodeURIComponent(bill.number)}`;
                  navigator.clipboard.writeText(publicUrl).then(() => {
                    toast.success('Public link copied to clipboard');
                  }).catch(() => {
                    const textArea = document.createElement('textarea');
                    textArea.value = publicUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    toast.success('Public link copied to clipboard');
                  });
                }}
              className="inline-flex items-center gap-2"
                title="Copy public link to bill"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            )}

            {/* Workspace-only: Remove from workspace */}
            {fromWorkspace && workspaceCanEdit && (
              <Button
                onClick={handleRemoveBillClick}
                variant="danger"
                icon={Trash2}
                title="Remove from workspace tracking"
              >
                Remove
              </Button>
            )}
          </div>
          
          {/* Workspace Context - Hidden but code preserved */}
          {/* Workspace Context - Hidden but code preserved
          {fromWorkspace && workspaceInfo && (
            <div className="col-span-4 flex items-center gap-2">
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Viewing in workspace:
              </span>
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 rounded-full">
                {workspaceInfo.name}
              </span>
            </div>
          )} */}

          {/* Row 3: Labels - Introduced, Subject Categories, Calendar Event */}
          <div className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Introduced</div>
          <div className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Subject Categories</div>
          <div className="lg:col-span-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Calendar Event</div>

          {/* Row 4: Data - Date, Subjects, Calendar Event */}
          <div className="text-text-primary dark:text-text-dark-primary">{formatDate(bill.introduced_date)}</div>
          <div className="text-text-primary dark:text-text-dark-primary">
            {bill.subject ? (
              (() => {
                const subjects = Array.isArray(bill.subject) 
                  ? bill.subject 
                  : bill.subject.split(',').map(s => s.trim()).filter(s => s);
                return subjects.join(', ');
              })()
            ) : (
              <span className="text-text-tertiary dark:text-text-dark-tertiary">None assigned</span>
            )}
          </div>
          <div className="lg:col-span-2">
            {billData?.nextCalendarEvent ? (
              <CalendarEventDisplay
                events={[billData.nextCalendarEvent]}
                billNumber={billData?.bill?.number}
                billTitle={billData?.bill?.title || billData?.bill?.concerning}
                variant="inline"
                context="authenticated"
                showIcon={false}
              />
            ) : (
              <span className="text-text-tertiary dark:text-text-dark-tertiary">No upcoming events</span>
            )}
          </div>

          {/* Row 5: Labels - Bill Sponsors */}
          <div className="col-span-1 lg:col-span-4 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Bill Sponsors</div>

          {/* Row 6: Data - Sponsors */}
          <div className="col-span-1 lg:col-span-4">
            {sponsors.length > 0 ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {(() => {
                  const uniqueSponsors = sponsors.reduce((acc, sponsor) => {
                    const key = `${sponsor.name}-${sponsor.sponsor_type}`;
                    if (!acc.has(key)) {
                      acc.set(key, sponsor);
                    }
                    return acc;
                  }, new Map<string, BillSponsor>());
                  
                  return Array.from(uniqueSponsors.values()).map((sponsor, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{sponsor.title}</span>
                      {sponsor.legislator_id ? (
                        <Link 
                          to={`/legislators/${sponsor.legislator_id}`}
                          className={`text-sm font-medium hover:underline cursor-pointer ${getPartyTextColor(sponsor.party)}`}
                        >
                          {sponsor.name}
                        </Link>
                      ) : (
                        <span className={`text-sm font-medium ${getPartyTextColor(sponsor.party)}`}>
                          {sponsor.name}
                        </span>
                      )}
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">({sponsor.sponsor_type})</span>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <span className="text-text-tertiary dark:text-text-dark-tertiary">None listed</span>
            )}
          </div>

          {/* Row 7: Labels - Committee Assignment, Last Action */}
          <div className="lg:col-span-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Latest Committee Assignment</div>
          <div className="lg:col-span-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Last Action</div>

          {/* Row 8: Data - Committee, Last Action */}
          <div className="lg:col-span-2 text-text-primary dark:text-text-dark-primary">
            {committees.length > 0 
              ? formatCommitteeName(committees[0].name, committees[0].chamber)
              : 'Not assigned'
            }
          </div>
          <div className="lg:col-span-2 text-text-primary dark:text-text-dark-primary">
            {formatDate(bill.latest_date)} - {bill.latest_action}
          </div>

          {/* Row 9-10: Description */}
          {bill.concerning && (
            <>
              <div className="col-span-1 lg:col-span-4 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Description</div>
              <div className="col-span-1 lg:col-span-4 p-4 bg-background-tertiary dark:bg-background-dark-tertiary border-l-4 border-primary-600 rounded-r-lg text-text-primary dark:text-text-dark-primary">
                {bill.concerning}
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Summary Section */}
      {billData?.aiSummary && (
        <AISummarySection aiSummary={billData.aiSummary} />
      )}

      {/* Bill Summary */}
      {bill.summary && (
        <div className="card">
          <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4 pb-2 border-b border-border-light dark:border-border-dark">
            Bill Summary
          </h3>
          <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">{bill.summary}</p>
        </div>
      )}

      {/* Tabbed Content */}
      {isMobile && !new URLSearchParams(location.search).get('section') ? (
        // MOBILE: Action menu (no section selected)
        (() => {
          const menuItems = tabs.map(tab => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
            route: `${location.pathname}?section=${tab.id}`,
            description: tab.id === 'history' ? 'View legislative timeline' :
                       tab.id === 'text' ? 'Read bill documents' :
                       tab.id === 'amendments' ? 'View proposed changes' :
                       tab.id === 'fiscal' ? 'See fiscal impact' :
                       tab.id === 'committees' ? 'Committee hearings' :
                       tab.id === 'lobbyists' ? 'Registered lobbyists' :
                       tab.id === 'votes' ? 'Voting records' : ''
          }));
          
          return (
            <MobileMenuPage
              menuItems={menuItems}
            />
          );
        })()
      ) : (
        // Show content (either mobile with section or desktop with tabs)
        <div className="card p-0 overflow-hidden">
          {/* Tab Navigation - Desktop Only */}
          {!isMobile && (
            <div className="bg-background-secondary px-4 dark:bg-background-dark-tertiary">
              <Tabs
                level="panel"
                ariaLabel="Bill detail sections"
                tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label, icon: tab.icon }))}
                activeId={activeTab}
                onChange={setActiveTab}
              />
            </div>
          )}

          {/* Tab Content - Both Mobile and Desktop */}
          <div className="p-6">
          {/* Bill History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Legislative History</h3>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                      <div className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary min-w-24">
                        {item.event_date}
                      </div>
                      <div className="text-sm font-medium text-primary-600 min-w-20">
                        {item.location}
                      </div>
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary flex-1">
                        {item.action}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No legislative history available
                </div>
              )}
            </div>
          )}

          {/* Bill Text Tab */}
          {activeTab === 'text' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Bill Text & Documents</h3>
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc, index) => {
                    // Create a meaningful title for the document
                    const getDocumentTitle = () => {
                      if (doc.type && doc.type !== 'N/A' && doc.type.trim() !== '') {
                        return doc.type;
                      }
                      if (doc.subtype && doc.subtype !== 'N/A' && doc.subtype.trim() !== '') {
                        return doc.subtype;
                      }
                      return 'Bill Document';
                    };

                    const getDocumentSubtitle = () => {
                      if (doc.subtype && doc.subtype !== 'N/A' && doc.subtype.trim() !== '' && doc.type && doc.type !== 'N/A' && doc.type.trim() !== '') {
                        return doc.subtype;
                      }
                      return null;
                    };

                    return (
                      <div key={index} className="card">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-text-primary dark:text-text-dark-primary">{getDocumentTitle()}</div>
                        </div>
                        {getDocumentSubtitle() && (
                          <div className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                            {getDocumentSubtitle()}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                            {doc.date && doc.date !== 'N/A' ? formatDate(doc.date) : 'Date not available'}
                          </div>
                          {doc.url && (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                            >
                              View Document <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No bill documents available
                </div>
              )}
            </div>
          )}

          {/* Amendments Tab */}
          {activeTab === 'amendments' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Bill Amendments</h3>
              {amendments && amendments.length > 0 ? (
                <div className="space-y-3">
                  {amendments.map((amendment, index) => (
                    <div key={index} className="card">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-text-primary dark:text-text-dark-primary">
                          Amendment {amendment.amendment_number}
                        </div>
                        {amendment.status && (
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            amendment.status.toLowerCase().includes('passed') || amendment.status.toLowerCase().includes('adopted')
                              ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
                              : amendment.status.toLowerCase().includes('failed') || amendment.status.toLowerCase().includes('rejected')
                              ? 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300'
                              : 'bg-background-tertiary text-text-primary dark:bg-background-dark-tertiary dark:text-text-dark-primary'
                          }`}>
                            {amendment.status}
                          </div>
                        )}
                      </div>
                      {amendment.committee_hearing && (
                        <div className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                          {amendment.committee_hearing}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                          {amendment.date ? formatDate(amendment.date) : 'Date not available'}
                        </div>
                        {amendment.url && (
                          <a
                            href={amendment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            View Amendment <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No amendments available for this bill
                </div>
              )}
            </div>
          )}

          {/* Fiscal Notes Tab */}
          {activeTab === 'fiscal' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Fiscal Impact Notes</h3>
              {fiscalNotes && fiscalNotes.length > 0 ? (
                <div className="space-y-3">
                  {fiscalNotes.map((note, index) => (
                    <div key={index} className="card">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-text-primary dark:text-text-dark-primary">
                          Fiscal Note - {note.version}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                          {formatDate(note.date)}
                        </div>
                        {note.url && (
                          <a
                            href={note.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            View Fiscal Note <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg border-2 border-dashed border-border-light dark:border-border-dark">
                  No fiscal impact notes available for this bill
                </div>
              )}
            </div>
          )}

          {/* Committees Tab */}
          {activeTab === 'committees' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Committee Information</h3>
              {committees.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* House Committees Column */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-text-primary dark:text-text-dark-primary border-b border-border-light dark:border-border-dark pb-2">
                      House Committees
                    </h4>
                    {(() => {
                      const houseCommittees = committees.filter(committee => 
                        committee.chamber.toLowerCase().includes('house')
                      );
                      
                      return houseCommittees.length > 0 ? (
                        <div className="space-y-3">
                          {houseCommittees.map((committee) => {
                            const isCommitteeExpanded = expandedCommittees.has(committee.id);
                            return (
                            <div key={committee.id} className="space-y-3">
                              {/* Committee Header - Clickable */}
                              <button
                                onClick={() => toggleCommitteeExpansion(committee.id)}
                                className="w-full card bg-background-tertiary dark:bg-background-dark-tertiary hover:bg-neutral-200 dark:hover:bg-background-dark-tertiary/80 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="font-semibold text-text-primary dark:text-text-dark-primary text-lg">
                                      {committee.name}
                                    </div>
                                    {committee.hearings && committee.hearings.length > 0 && (
                                      <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                                        ({committee.hearings.length} {committee.hearings.length === 1 ? 'meeting' : 'meetings'})
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCommitteeChampberDisplay(committee.chamber).className}`}>
                                      {getCommitteeChampberDisplay(committee.chamber).label}
                                    </span>
                                    {isCommitteeExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-primary-600" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                                    )}
                                  </div>
                                </div>
                              </button>

                              {/* Committee Hearings - Show when expanded */}
                              {isCommitteeExpanded && committee.hearings && committee.hearings.length > 0 && (
                                <div className="space-y-3 ml-4">
                                  {committee.hearings.map((hearing) => {
                                    const isExpanded = expandedCommitteeHearings.has(hearing.id);
                                    const voteBreakdown = hearing.votes?.reduce((acc, v) => {
                                      const vote = v.vote?.toUpperCase();
                                      if (vote === 'Y' || vote === 'YES') acc.aye++;
                                      else if (vote === 'N' || vote === 'NO') acc.no++;
                                      else if (vote === 'E' || vote === 'EXCUSED') acc.excused++;
                                      else if (vote === 'A' || vote === 'V' || vote === 'ABSENT') acc.absent++;
                                      return acc;
                                    }, { aye: 0, no: 0, excused: 0, absent: 0 }) || { aye: 0, no: 0, excused: 0, absent: 0 };
                                    const hasVotes = voteBreakdown.aye > 0 || voteBreakdown.no > 0 || voteBreakdown.excused > 0 || voteBreakdown.absent > 0;

                                    return (
                                      <div key={hearing.id} className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                                        {/* Hearing Summary Header */}
                                        <div className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary">
                                          <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-3 mb-1">
                                                <div className="font-semibold text-text-primary dark:text-text-dark-primary">
                                                  {formatDate(hearing.hearing_date)}
                                                </div>
                                                {hearing.action && (
                                                  <div className="px-2 py-1 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200">
                                                    {hearing.action}
                                                  </div>
                                                )}
                                              </div>
                                              {hearing.activity && (
                                                <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                                                  {hearing.activity}
                                                </div>
                                              )}
                                              {hearing.vote && (
                                                <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                                                  Result: {hearing.vote}
                                                </div>
                                              )}
                                              {(hearing.moved_by || hearing.seconded_by) && (
                                                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mt-1">
                                                  {hearing.moved_by && `Moved by: ${hearing.moved_by}`}
                                                  {hearing.moved_by && hearing.seconded_by && ' | '}
                                                  {hearing.seconded_by && `Seconded by: ${hearing.seconded_by}`}
                                                </div>
                                              )}
                                            </div>
                                            {hasVotes && (
                                              <button
                                                onClick={() => toggleCommitteeHearingExpansion(hearing.id)}
                                                className="flex items-center gap-2 px-3 py-1 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                                              >
                                                {isExpanded ? (
                                                  <>
                                                    Hide Votes <ChevronUp className="w-4 h-4" />
                                                  </>
                                                ) : (
                                                  <>
                                                    Show Votes <ChevronDown className="w-4 h-4" />
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </div>

                                          {/* Vote Count Summary */}
                                          {hasVotes && (
                                            <BillVoteSummary
                                              aye={voteBreakdown.aye}
                                              no={voteBreakdown.no}
                                              excused={voteBreakdown.excused}
                                              absent={voteBreakdown.absent}
                                              showPercentages={false}
                                              className="mt-3"
                                            />
                                          )}
                                        </div>

                                        {/* Individual Committee Member Votes */}
                                        {isExpanded && hasVotes && (
                                          <div className="p-4 bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark">
                                            <h4 className="font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                                              Committee Member Votes
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                              {hearing.votes.map((memberVote, memberIndex) => (
                                                <div key={memberIndex} className="flex justify-between items-center py-2 px-3 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded">
                                                  {memberVote.legislator_id ? (
                                                    <Link 
                                                      to={`/legislators/${memberVote.legislator_id}`}
                                                      className={`text-sm font-medium hover:underline cursor-pointer ${getPartyTextColor(memberVote.party)}`}
                                                    >
                                                      {memberVote.member_name}
                                                    </Link>
                                                  ) : (
                                                    <span className={`text-sm font-medium ${getPartyTextColor(memberVote.party)}`}>
                                                      {memberVote.member_name}
                                                    </span>
                                                  )}
                                                  <span className={`text-sm ${getVoteColor(memberVote.vote)}`}>
                                                    {formatVoteDisplay(memberVote.vote)}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* No votes message */}
                                        {isExpanded && !hasVotes && (
                                          <div className="p-4 bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark text-center text-text-tertiary dark:text-text-dark-tertiary">
                                            No individual member votes recorded for this hearing
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {isCommitteeExpanded && (!committee.hearings || committee.hearings.length === 0) && (
                                <div className="ml-4 text-sm text-text-tertiary dark:text-text-dark-tertiary italic">
                                  No hearings recorded
                                </div>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-text-tertiary dark:text-text-dark-tertiary italic">
                          No House committees
                        </div>
                      );
                    })()}
                  </div>

                  {/* Senate Committees Column */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-text-primary dark:text-text-dark-primary border-b border-border-light dark:border-border-dark pb-2">
                      Senate Committees
                    </h4>
                    {(() => {
                      const senateCommittees = committees.filter(committee => 
                        committee.chamber.toLowerCase().includes('senate')
                      );
                      
                      return senateCommittees.length > 0 ? (
                        <div className="space-y-3">
                          {senateCommittees.map((committee) => {
                            const isCommitteeExpanded = expandedCommittees.has(committee.id);
                            return (
                            <div key={committee.id} className="space-y-3">
                              {/* Committee Header - Clickable */}
                              <button
                                onClick={() => toggleCommitteeExpansion(committee.id)}
                                className="w-full card bg-background-tertiary dark:bg-background-dark-tertiary hover:bg-neutral-200 dark:hover:bg-background-dark-tertiary/80 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="font-semibold text-text-primary dark:text-text-dark-primary text-lg">
                                      {committee.name}
                                    </div>
                                    {committee.hearings && committee.hearings.length > 0 && (
                                      <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                                        ({committee.hearings.length} {committee.hearings.length === 1 ? 'meeting' : 'meetings'})
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCommitteeChampberDisplay(committee.chamber).className}`}>
                                      {getCommitteeChampberDisplay(committee.chamber).label}
                                    </span>
                                    {isCommitteeExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-primary-600" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                                    )}
                                  </div>
                                </div>
                              </button>

                              {/* Committee Hearings - Show when expanded */}
                              {isCommitteeExpanded && committee.hearings && committee.hearings.length > 0 && (
                                <div className="space-y-3 ml-4">
                                  {committee.hearings.map((hearing) => {
                                    const isExpanded = expandedCommitteeHearings.has(hearing.id);
                                    const voteBreakdown = hearing.votes?.reduce((acc, v) => {
                                      const vote = v.vote?.toUpperCase();
                                      if (vote === 'Y' || vote === 'YES') acc.aye++;
                                      else if (vote === 'N' || vote === 'NO') acc.no++;
                                      else if (vote === 'E' || vote === 'EXCUSED') acc.excused++;
                                      else if (vote === 'A' || vote === 'V' || vote === 'ABSENT') acc.absent++;
                                      return acc;
                                    }, { aye: 0, no: 0, excused: 0, absent: 0 }) || { aye: 0, no: 0, excused: 0, absent: 0 };
                                    const hasVotes = voteBreakdown.aye > 0 || voteBreakdown.no > 0 || voteBreakdown.excused > 0 || voteBreakdown.absent > 0;

                                    return (
                                      <div key={hearing.id} className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                                        {/* Hearing Summary Header */}
                                        <div className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary">
                                          <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-3 mb-1">
                                                <div className="font-semibold text-text-primary dark:text-text-dark-primary">
                                                  {formatDate(hearing.hearing_date)}
                                                </div>
                                                {hearing.action && (
                                                  <div className="px-2 py-1 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200">
                                                    {hearing.action}
                                                  </div>
                                                )}
                                              </div>
                                              {hearing.activity && (
                                                <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                                                  {hearing.activity}
                                                </div>
                                              )}
                                              {hearing.vote && (
                                                <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                                                  Result: {hearing.vote}
                                                </div>
                                              )}
                                              {(hearing.moved_by || hearing.seconded_by) && (
                                                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mt-1">
                                                  {hearing.moved_by && `Moved by: ${hearing.moved_by}`}
                                                  {hearing.moved_by && hearing.seconded_by && ' | '}
                                                  {hearing.seconded_by && `Seconded by: ${hearing.seconded_by}`}
                                                </div>
                                              )}
                                            </div>
                                            {hasVotes && (
                                              <button
                                                onClick={() => toggleCommitteeHearingExpansion(hearing.id)}
                                                className="flex items-center gap-2 px-3 py-1 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                                              >
                                                {isExpanded ? (
                                                  <>
                                                    Hide Votes <ChevronUp className="w-4 h-4" />
                                                  </>
                                                ) : (
                                                  <>
                                                    Show Votes <ChevronDown className="w-4 h-4" />
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </div>

                                          {/* Vote Count Summary */}
                                          {hasVotes && (
                                            <BillVoteSummary
                                              aye={voteBreakdown.aye}
                                              no={voteBreakdown.no}
                                              excused={voteBreakdown.excused}
                                              absent={voteBreakdown.absent}
                                              showPercentages={false}
                                              className="mt-3"
                                            />
                                          )}
                                        </div>

                                        {/* Individual Committee Member Votes */}
                                        {isExpanded && hasVotes && (
                                          <div className="p-4 bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark">
                                            <h4 className="font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                                              Committee Member Votes
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                              {hearing.votes.map((memberVote, memberIndex) => (
                                                <div key={memberIndex} className="flex justify-between items-center py-2 px-3 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded">
                                                  {memberVote.legislator_id ? (
                                                    <Link 
                                                      to={`/legislators/${memberVote.legislator_id}`}
                                                      className={`text-sm font-medium hover:underline cursor-pointer ${getPartyTextColor(memberVote.party)}`}
                                                    >
                                                      {memberVote.member_name}
                                                    </Link>
                                                  ) : (
                                                    <span className={`text-sm font-medium ${getPartyTextColor(memberVote.party)}`}>
                                                      {memberVote.member_name}
                                                    </span>
                                                  )}
                                                  <span className={`text-sm ${getVoteColor(memberVote.vote)}`}>
                                                    {formatVoteDisplay(memberVote.vote)}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* No votes message */}
                                        {isExpanded && !hasVotes && (
                                          <div className="p-4 bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark text-center text-text-tertiary dark:text-text-dark-tertiary">
                                            No individual member votes recorded for this hearing
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {isCommitteeExpanded && (!committee.hearings || committee.hearings.length === 0) && (
                                <div className="ml-4 text-sm text-text-tertiary dark:text-text-dark-tertiary italic">
                                  No hearings recorded
                                </div>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-text-tertiary dark:text-text-dark-tertiary italic">
                          No Senate committees
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No committee information available
                </div>
              )}
            </div>
          )}

          {/* Lobbyists Tab */}
          {activeTab === 'lobbyists' && (
            <div ref={lobbyistsTabRef} className="space-y-6">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Registered Lobbyists</h3>
              
              {lobbyists && lobbyists.length > 0 ? (
                <>
                  {/* Summary Metrics */}
                  <div className="card bg-background-tertiary dark:bg-background-dark-tertiary">
                    {/* First Row - Overall Metrics */}
                    <StatCardRow className="mb-6">
                      <StatCard
                        value={new Set(latestLobbyistRecords.map(l => l.lobbyist_name)).size}
                        label="Registered Lobbyists"
                      />
                      <StatCard
                        value={latestLobbyistRecords.reduce((acc, curr) => {
                          const firmKey = curr.lobbyist_firm_name || 'Individual';
                          return acc.has(firmKey) ? acc : acc.add(firmKey);
                        }, new Set()).size}
                        label="Lobbying Firms"
                      />
                      <StatCard
                        value={new Set(latestLobbyistRecords.map(l => l.client_name)).size}
                        label="Unique Clients"
                      />
                    </StatCardRow>

                    {/* Second Row - Position Totals */}
                    <StatCardRow className="pt-6 border-t border-border-light dark:border-border-dark">
                      <StatCard
                        value={latestLobbyistRecords.filter(l => l.position?.toLowerCase() === 'supporting').length}
                        label="Supporting"
                        variant="success"
                      />
                      <StatCard
                        value={latestLobbyistRecords.filter(l => l.position?.toLowerCase() === 'opposing').length}
                        label="Opposing"
                        variant="danger"
                      />
                      <StatCard
                        value={latestLobbyistRecords.filter(l => l.position?.toLowerCase() === 'amending').length}
                        label="Amending"
                        variant="warning"
                      />
                      <StatCard
                        value={latestLobbyistRecords.filter(l => l.position?.toLowerCase() === 'monitoring').length}
                        label="Monitoring"
                        variant="info"
                      />
                    </StatCardRow>
                  </div>

                  {/* Lobbying activity — grouped/expandable explorer */}
                  <LobbyingExplorer
                    scope={{ bill_number: bill.number }}
                    defaultGroupBy="lobbyist"
                    groupOptions={BILL_LOBBY_GROUP_OPTIONS}
                    caveat="Grouped for this bill; expand a row to see each filing’s lobbyist, client, position, and report month. A lobbyist may file for multiple clients."
                  />
                </>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg border-2 border-dashed border-border-light dark:border-border-dark">
                  No registered lobbyists found for this bill
                </div>
              )}
            </div>
          )}

          {/* Votes Tab */}
          {activeTab === 'votes' && (
            <div ref={votesTabRef} className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Voting Records</h3>
              {chamberVotes.length > 0 ? (
                <div className="space-y-4">
                  {chamberVotes.map((vote, index) => {
                    const isExpanded = expandedVotes.has(vote.id);
                    const totalVotes = vote.aye + vote.no + vote.excused + vote.absent;
                    const ayePercent = totalVotes > 0 ? Math.round((vote.aye / totalVotes) * 100) : 0;
                    const noPercent = totalVotes > 0 ? Math.round((vote.no / totalVotes) * 100) : 0;
                    
                    return (
                      <div key={index} className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                        {/* Vote Summary Header */}
                        <div className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <div className="font-semibold text-text-primary dark:text-text-dark-primary text-lg">
                                  {vote.chamber} - {vote.action}
                                </div>
                                {vote.vote_type && (
                                  <div className={`px-2 py-1 rounded text-xs font-medium text-white`}
                                       style={{
                                         backgroundColor: 
                                           vote.vote_type === 'committee' ? '#8B5A2B' :
                                           vote.vote_type === 'final_passage' ? '#DC2626' :
                                           vote.vote_type === 'amendment' ? '#7C3AED' :
                                           '#6B7280'
                                       }}>
                                    {vote.vote_type === 'committee' ? 'Committee' :
                                     vote.vote_type === 'final_passage' ? 'Final Passage' :
                                     vote.vote_type === 'amendment' ? 'Amendment' :
                                     vote.vote_type === 'second_reading' ? '2nd Reading' :
                                     vote.vote_type === 'first_reading' ? '1st Reading' :
                                     'Other'}
                                  </div>
                                )}
                              </div>
                              <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">{vote.date}</div>
                              <div className="text-sm text-text-secondary dark:text-text-dark-secondary">{vote.motion}</div>
                            </div>
                            {vote.memberVotes && vote.memberVotes.length > 0 ? (
                              <button
                                onClick={() => toggleVoteExpansion(vote.id)}
                                className="flex items-center gap-2 px-3 py-1 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                              >
                                {isExpanded ? (
                                  <>
                                    Hide Details <ChevronUp className="w-4 h-4" />
                                  </>
                                ) : (
                                  <>
                                    Show Details <ChevronDown className="w-4 h-4" />
                                  </>
                                )}
                              </button>
                            ) : (
                              <div className="px-3 py-1 text-sm text-text-tertiary dark:text-text-dark-tertiary border border-border-light dark:border-border-dark rounded-lg">
                                No individual votes available
                              </div>
                            )}
                          </div>

                          {/* Vote Count Summary */}
                          <StatCardRow>
                            <StatCard value={vote.aye} label={`Yes (${ayePercent}%)`} variant="success" />
                            <StatCard value={vote.no} label={`No (${noPercent}%)`} variant="danger" />
                            <StatCard value={vote.excused} label="Excused" variant="warning" />
                            <StatCard value={vote.absent} label="Absent" />
                          </StatCardRow>
                        </div>

                        {/* Individual Member Votes */}
                        {isExpanded && vote.memberVotes && vote.memberVotes.length > 0 && (
                          <div className="p-4 bg-white border-t border-border-light dark:border-border-dark">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-text-primary dark:text-text-dark-primary">
                                Votes by {vote.chamber} member
                              </h4>
                              {vote.vote_type && (
                                <div className={`px-3 py-1 rounded-full text-xs font-medium text-white`}
                                     style={{
                                       backgroundColor: 
                                         vote.vote_type === 'committee' ? '#8B5A2B' :
                                         vote.vote_type === 'final_passage' ? '#DC2626' :
                                         vote.vote_type === 'amendment' ? '#7C3AED' :
                                         '#6B7280'
                                     }}>
                                  {vote.vote_type === 'committee' ? 'Committee Vote' :
                                   vote.vote_type === 'final_passage' ? 'Final Passage' :
                                   vote.vote_type === 'amendment' ? 'Amendment Vote' :
                                   vote.vote_type === 'second_reading' ? 'Second Reading' :
                                   vote.vote_type === 'first_reading' ? 'First Reading' :
                                   'Other Vote'}
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {vote.memberVotes.map((memberVote, memberIndex) => (
                                <div key={memberIndex} className="flex justify-between items-center py-2 px-3 hover:bg-background-tertiary dark:bg-background-dark-tertiary rounded">
                                  {memberVote.legislator_id ? (
                                    <Link 
                                      to={`/legislators/${memberVote.legislator_id}`}
                                      state={{ fromBill: bill.number, billId: bill.id }}
                                      className={`text-sm font-medium hover:underline cursor-pointer ${getPartyTextColor(memberVote.party)}`}
                                    >
                                      {memberVote.member_name}
                                    </Link>
                                  ) : (
                                    <span className={`text-sm font-medium ${getPartyTextColor(memberVote.party)}`}>
                                      {memberVote.member_name}
                                    </span>
                                  )}
                                  <span className={`text-sm ${getVoteColor(memberVote.vote)}`}>
                                    {formatVoteDisplay(memberVote.vote)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No individual votes message */}
                        {isExpanded && (!vote.memberVotes || vote.memberVotes.length === 0) && (
                          <div className="p-4 bg-white border-t border-border-light dark:border-border-dark text-center text-text-tertiary dark:text-text-dark-tertiary">
                            Individual member votes not available for this vote
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No voting records available
                </div>
              )}
            </div>
          )}

          {/* Sponsors Tab - Removed since sponsors are now displayed in the top pane */}

          {/* Session Laws Tab */}
          {activeTab === 'laws' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Session Laws</h3>
              {sessionLaws.length > 0 ? (
                <div className="space-y-3">
                  {sessionLaws.map((law, index) => (
                    <div key={index} className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                      <div className="font-medium text-text-primary dark:text-text-dark-primary">Chapter {law.chapter}</div>
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary">Effective: {law.effective_date}</div>
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">{law.title}</div>
                      {law.document && (
                        <a
                          href={law.document}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mt-2"
                        >
                          View Document <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
                  No session law information available
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
      
      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}


      {/* Workspace Selection Modal */}
      <WorkspaceSelectionModal
        isOpen={showBookmarkModal}
        onClose={handleBookmarkModalClose}
        entityId={bill?.id?.toString() || ''}
        entityLabel={bill.number}
        onSave={handleBookmarkSuccess}
      />

      {/* Vote Predictions Modal */}
      {/*
        The same dialog the Bill Tracking tab opens, not the two-step wizard this
        page used to mount. The wizard exists for the case where no workspace is
        known and one has to be chosen before predictions can be saved — but the
        Votes button on this page only renders when `fromWorkspace` is set, so
        that case never arose here. It was asking which workspace to save to when
        the answer was already in the URL, and showing a stripped-down table to
        do it.
      */}
      {fromWorkspace && workspacePermissions && (
        <VotePredictionsModal
          isOpen={showVotePredictionsModal}
          onClose={() => setShowVotePredictionsModal(false)}
          workspaceId={fromWorkspace}
          permissions={workspacePermissions}
          billId={bill?.id?.toString() || ''}
          billNumber={bill?.number || ''}
          billTitle={bill?.title || ''}
        />
      )}

      {/* Stop Tracking Confirmation Dialog */}
      {showStopTrackingDialog && fromWorkspace && (
        <StopTrackingConfirmDialog
          isOpen={showStopTrackingDialog}
          onConfirm={handleStopTrackingConfirm}
          onClose={handleStopTrackingCancel}
          billNumber={bill?.number || ''}
          workspaceName={workspaceInfo?.name || 'Workspace'}
          isLoading={isRemovingBill}
        />
      )}
      
    </PageShell>
  );
};

export default BillDetailPage; 